import { Action } from '@ngrx/store';
import { CaEntityState, INITIAL_ENTITY_STATE } from './entity-state';
import { BaseEntityType, BaseFilterType, CaEntityActionsFactory } from './entity-actions-factory';
import { createReducer, on } from '@ngrx/store';
import { ResponseSuccessAuthLogoutAction } from '../actions';
import { mergeReducers } from './merge-reducers';

export class CaEntityReducer<EntityType extends BaseEntityType, FilterType extends BaseFilterType> {
  readonly initialState: CaEntityState<EntityType> = {
    ...INITIAL_ENTITY_STATE,
  };

  constructor(public actionClass: CaEntityActionsFactory<EntityType, any, any, FilterType>) {}

  reducer(state: CaEntityState<EntityType>, action: Action): CaEntityState<EntityType> {
    return this.entityReducer(state, action);
  }

  private allReducer = createReducer(
    this.initialState,
    on(this.actionClass.RequestRetrieveAllAction, (state) => ({ ...state, loadingAll: true })),
    on(this.actionClass.PageChangeAction, (state, { page }) => ({
      ...state,
      page: { ...page, isDefault: false },
    })),
    on(this.actionClass.PageChangeBySegmentAction, (state, { segment, page }) => {
      return {
        ...state,
        paginators: {
          ...(state.paginators ?? {}),
          [segment]: {
            ...state.paginators[segment],
            page: page,
            filter: { ...state.paginators[segment]?.filter, isDefault: false },
          },
        },
      };
    }),
    on(this.actionClass.UpdateFilterAction, (state, { filter, isDefault }) => {
      return {
        ...state,
        filter: {
          ...filter,
          order: filter?.order || (state.filter || {}).order,
          isDefault: isDefault ?? false,
        },
        page: { ...this.initialState.page },
      };
    }),
    on(this.actionClass.UpdateFilterForSegmentAction, (state, { segment, filter, isDefault }) => {
      const existingFilter = state.paginators[segment]?.filter ?? {};

      return {
        ...state,
        paginators: {
          ...state.paginators,
          [segment]: {
            ...state.paginators[segment],
            page: {
              ...this.initialState.page,
              pageSize:
                state.paginators[segment]?.page?.pageSize || this.initialState.page.pageSize,
            },
            filter: {
              ...filter,
              order: filter?.order || (existingFilter || {}).order,
              paginatorId: undefined,
              isDefault: isDefault ?? false,
            },
          },
        },
      };
    }),
    on(this.actionClass.ResetPageAction, (state) => ({
      ...state,
      page: { ...this.initialState.page },
    })),

    on(this.actionClass.ResponseSuccessCountAction, (state, { count }) => ({
      ...state,
      page: { ...state.page, length: count },
    })),

    on(this.actionClass.RequestSearchAction, (state) => ({
      ...state,
      searching: true,
      searchResults: [],
    })),
    on(this.actionClass.ResponseSuccessSearchAction, (state, { payload }) => {
      const searchResults = payload.map((org) => org.id);
      const ids = Array.from(new Set(state.ids.concat(searchResults)));
      const entities = {
        ...state.entities,
        ...payload.reduce((acc: any, entity: any) => ({ ...acc, [entity.id]: entity }), {}),
      };
      return { ...state, searching: false, ids, entities, searchResults };
    }),
    on(this.actionClass.ResponseFailSearchAction, (state) => ({
      ...state,
      searching: false,
    })),
    on(this.actionClass.ResponseSuccessRetrieveAllAction, (state, { payload }) => {
      const ids = payload.map((org) => org.id);
      const time = new Date().valueOf();
      const updatedAt = payload.reduce(
        (acc: any, entity: any) => ({ ...acc, [entity.id]: time }),
        {}
      );
      const entities = payload.reduce(
        (acc: any, entity: any) => ({ ...acc, [entity.id]: entity }),
        {}
      );
      return { ...state, loadingAll: false, ids, entities, updatedAt };
    }),
    on(this.actionClass.ResponseFailRetrieveAllAction, (state, { error }) => ({
      ...state,
      loadingAll: false,
      error,
    })),
  );
  private segmentReducer = createReducer(
    this.initialState,
    on(this.actionClass.RequestAllForSegmentAction, (state, { segment }) => {
      const paginators = state.paginators || {};
      const paginator = {
        [segment]: {
          ...paginators[segment],
          loading: true,
          page: paginators[segment]?.page || { ...this.initialState.page },
        },
      };
      return { ...state, paginators: { ...paginators, ...paginator } };
    }),
    on(this.actionClass.ResponseSuccessAllForSegmentAction, (state, { segment, filter, items }) => {
      const time = new Date().valueOf();
      const updatedAt = items.reduce(
        (acc: any, entity: any) => ({ ...acc, [entity.id]: time }),
        {}
      );
      const entities = items.reduce(
        (acc: any, entity: any) => ({ ...acc, [entity.id]: entity }),
        {}
      );
      const paginators = state.paginators;
      const complete = items.length < filter.limit;
      const skip = filter.skip || 0;
      const ids = state.paginators[segment] ? state.paginators[segment].ids || [] : [];
      const paginatorIds = [...ids];

      const newIds = items.map((item) => item.id);
      const allIds = Array.from(new Set([...state.ids, ...newIds]));

      for (let i = 0; i < items.length; i++) {
        paginatorIds[i + skip] = items[i].id;
      }

      return {
        ...state,
        entities: { ...state.entities, ...entities },
        ids: allIds,
        updatedAt: { ...state.updatedAt, ...updatedAt },
        paginators: {
          ...paginators,
          [segment]: {
            ...(paginators[segment] ?? {}),
            ids: paginatorIds,
            complete,
            loading: false,
            page: paginators[segment]?.page || { ...this.initialState.page },
          },
        },
      };
    }),
    on(this.actionClass.ResponseFailAllForSegmentAction, (state, { segment, error }) => {
      const paginators = state.paginators;
      const paginator = {
        [segment]: {
          ...paginators[segment],
          page: paginators[segment]?.page || { ...this.initialState.page },
          loading: false,
          error,
        },
      };

      return { ...state, paginators: { ...paginators, ...paginator } };
    })
  );

  private byIdReducer = createReducer(
    this.initialState,
    on(this.actionClass.RequestCreateAction, (state) => ({ ...state, creating: true })),
    on(this.actionClass.RequestUpdateAction, (state, { id }) => ({
      ...state,
      loadingEntities: { ...state.loadingEntities, [id]: true },
    })),
    on(this.actionClass.RequestRetrieveByIdAction, (state, { id }) => ({
      ...state,
      loadingEntities: { ...state.loadingEntities, [id]: true },
    })),
    on(this.actionClass.RequestDeleteByIdAction, (state, { id }) => ({
      ...state,
      loadingEntities: { ...state.loadingEntities, [id]: true },
    })),

    on(this.actionClass.ResponseSuccessRetrieveCachedByIdAction, (state, { payload }) => ({
      ...state,
      loadingEntities: {
        ...state.loadingEntities,
        [payload.id]: false,
      },
    })),

    on(this.actionClass.ResponseSuccessUpdateAction, (state, { payload }) => {
      const ids = Array.from(new Set([...state.ids, payload.id]));
      const entities = {
        ...state.entities,
        [payload.id]: payload,
      };
      const updatedAt = {
        ...state.updatedAt,
        [payload.id]: new Date().valueOf(),
      };
      return {
        ...state,
        loadingEntities: {
          ...state.loadingEntities,
          [payload.id]: false,
        },
        updatedAt,
        creating: false,
        ids: ids,
        entities: entities,
      };
    }),
    on(this.actionClass.ResponseSuccessCreateAction, (state, { payload }) => {
      const ids = Array.from(new Set([...state.ids, payload.id]));
      const entities = {
        ...state.entities,
        [payload.id]: payload,
      };
      const updatedAt = {
        ...state.updatedAt,
        [payload.id]: new Date().valueOf(),
      };
      return {
        ...state,
        loadingEntities: {
          ...state.loadingEntities,
          [payload.id]: false,
        },
        updatedAt,
        creating: false,
        ids: ids,
        entities: entities,
      };
    }),
    on(this.actionClass.ResponseSuccessRetrieveByIdAction, (state, { payload }) => {
      const ids = Array.from(new Set([...state.ids, payload.id]));
      const entities = {
        ...state.entities,
        [payload.id]: payload,
      };
      const updatedAt = {
        ...state.updatedAt,
        [payload.id]: new Date().valueOf(),
      };
      return {
        ...state,
        loadingEntities: {
          ...state.loadingEntities,
          [payload.id]: false,
        },
        updatedAt,
        creating: false,
        ids: ids,
        entities: entities,
      };
    }),
    on(this.actionClass.ResponseFailCreateAction, (state) => ({ ...state, creating: false })),
    on(this.actionClass.ResponseSuccessDeleteByIdAction, (state, { id }) => {
      const ids = new Set(state.ids);
      ids.delete(id);
      const entities = { ...state.entities };
      delete entities[id];
      const updatedAt = { ...state.updatedAt };
      delete updatedAt[id];
      return {
        ...state,
        ids: Array.from(ids),
        entities,
        loadingEntities: {
          ...state.loadingEntities,
          [id]: false,
        },
        updatedAt,
      };
    }),
    on(this.actionClass.ResponseFailDeleteByIdAction, (state, { id }) => ({
      ...state,
      loadingEntities: {
        ...state.loadingEntities,
        [id]: false,
      },
    })),
    on(this.actionClass.ResponseFailRetrieveByIdAction, (state, { id }) => ({
      ...state,
      loadingEntities: {
        ...state.loadingEntities,
        [id]: false,
      },
    })),
    on(this.actionClass.ResponseFailUpdateAction, (state, { id }) => ({
      ...state,
      loadingEntities: {
        ...state.loadingEntities,
        [id]: false,
      },
    }))
  );

  private miscReducer = createReducer(
    this.initialState,
    on(this.actionClass.SelectByIdAction, (state, { id }) => ({ ...state, selectedId: id })),
    on(this.actionClass.ClearSegmentAction, (state, { segment }) => {
      const paginators = { ...state.paginators };
      delete paginators[segment];
      return { ...state, paginators };
    }),
    on(this.actionClass.ClearSegmentIdsAction, (state, { segment }) => {
      const paginators = { ...state.paginators };
      const thisPaginator = { ...paginators[segment] };
      thisPaginator.ids = [];
      thisPaginator.complete = false;
      thisPaginator.loading = false;
      return {
        ...state,
        paginators: { ...paginators, [segment]: thisPaginator },
      };
    }),
    on(ResponseSuccessAuthLogoutAction, () => this.initialState)
  );

  entityReducer = mergeReducers(
    this.allReducer,
    this.segmentReducer,
    this.byIdReducer,
    this.miscReducer
  );
}
